import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Loader2, Save, ChevronLeft, Mail, Lock } from 'lucide-react';
import { isValidPhoneNumber } from 'libphonenumber-js';

import PortalLayout from './PortalLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { usePortalAuth } from '../../context/PortalAuthContext';
import { portalMeAPI } from '../../lib/portalApi';
import CountrySelect from '../../components/portal/CountrySelect';
import PortalPhoneInput from '../../components/portal/PortalPhoneInput';

const REQUIRED_FIELDS = ['company_name', 'contact_name', 'phone', 'address_line1', 'postal_code', 'city', 'country_code'];

const PortalProfile = () => {
    const { t, i18n } = useTranslation('portal');
    const navigate = useNavigate();
    const { profile, email, refresh, loading } = usePortalAuth();
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);
    const [phoneInvalid, setPhoneInvalid] = useState(false);

    useEffect(() => {
        if (!profile) return;
        setForm({
            company_name:    profile.company_name    || '',
            vat_number:      profile.vat_number      || '',
            siret_number:    profile.siret_number    || '',
            contact_name:    profile.contact_name    || '',
            phone:           profile.phone           || '',
            address_line1:   profile.address_line1   || '',
            address_line2:   profile.address_line2   || '',
            postal_code:     profile.postal_code     || '',
            city:            profile.city            || '',
            country:         profile.country         || '',
            country_code:    profile.country_code    || (profile.country === 'France' ? 'FR' : ''),
            preferred_locale: profile.preferred_locale || i18n.resolvedLanguage || 'en',
        });
    }, [profile, i18n.resolvedLanguage]);

    const setField = (key) => (e) => {
        const value = typeof e === 'string' ? e : e?.target?.value;
        setForm((f) => ({ ...f, [key]: value }));
    };

    // Friendly field-label lookup used for the "Missing fields" toast.
    const labelFor = useMemo(() => ({
        company_name:  t('profile.field_company'),
        contact_name:  t('profile.field_contact'),
        phone:         t('profile.field_phone'),
        address_line1: t('profile.field_address1'),
        postal_code:   t('profile.field_postal'),
        city:          t('profile.field_city'),
        country_code:  t('profile.field_country'),
    }), [t]);

    const onSubmit = async (e) => {
        e.preventDefault();
        const missing = REQUIRED_FIELDS.filter((k) => !(form[k] || '').toString().trim());
        if (missing.length) {
            const names = missing.map((k) => (labelFor[k] || k).replace(' *', '')).join(', ');
            toast.error(t('profile.missing_fields', { fields: names }));
            return;
        }
        if (form.phone && !isValidPhoneNumber(form.phone)) {
            setPhoneInvalid(true);
            toast.error(t('profile.phone_invalid'));
            return;
        }
        setPhoneInvalid(false);

        setSaving(true);
        try {
            await portalMeAPI.update(form);
            if (form.preferred_locale && form.preferred_locale !== i18n.resolvedLanguage) {
                i18n.changeLanguage(form.preferred_locale);
            }
            await refresh();
            toast.success(t('profile.saved'));
            navigate('/portal', { replace: true });
        } catch (err) {
            toast.error(err.response?.data?.detail || t('profile.save_failed'));
        } finally {
            setSaving(false);
        }
    };

    const fieldClass = "bg-[#FBF8F6] border-[#EFE5E1] focus:border-[#C08478] focus:ring-[#C08478]/20";

    return (
        <PortalLayout>
            <div className="space-y-6" data-testid="portal-profile-page">
                <button
                    onClick={() => navigate(-1)}
                    className="text-sm text-gray-500 hover:text-[#1A1A1A] flex items-center gap-1"
                    data-testid="portal-profile-back-btn"
                >
                    <ChevronLeft size={16} /> {t('common.back')}
                </button>

                <div>
                    <p className="text-xs uppercase tracking-[3px] text-[#C08478] font-semibold mb-2">{t('profile.section_label')}</p>
                    <h1 className="text-3xl font-bold text-[#1A1A1A] mb-1">{t('profile.title')}</h1>
                    <p className="text-sm text-gray-500">{t('profile.subtitle')}</p>
                </div>

                <form onSubmit={onSubmit} noValidate className="bg-white border border-[#EFE5E1] rounded-lg p-6 sm:p-8" data-testid="portal-profile-form">
                    {/* Account email — read-only identity field */}
                    <div className="mb-6 flex items-start justify-between gap-4 p-4 rounded-md bg-[#FBF8F6] border border-[#EFE5E1]">
                        <div>
                            <Label className="text-xs uppercase tracking-wider text-gray-500 flex items-center gap-1.5">
                                <Mail size={11} /> {t('profile.account_email')}
                            </Label>
                            <div className="mt-1 text-[15px] font-medium text-[#1A1A1A]" data-testid="portal-profile-email">{email}</div>
                        </div>
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[2px] text-[#C08478] mt-1">
                            <Lock size={10} /> {t('profile.email_readonly')}
                        </span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-6 gap-5">
                        {/* Company */}
                        <div className="sm:col-span-6 space-y-1.5">
                            <Label htmlFor="pf-company_name" className="text-sm font-medium text-gray-700">
                                {t('profile.field_company')}
                            </Label>
                            <Input
                                id="pf-company_name"
                                data-testid="portal-profile-company-name"
                                value={form.company_name || ''}
                                onChange={setField('company_name')}
                                disabled={loading || saving}
                                className={fieldClass}
                                required
                            />
                        </div>

                        {/* TVA / SIRET — now two distinct fields */}
                        <div className="sm:col-span-3 space-y-1.5">
                            <Label htmlFor="pf-vat" className="text-sm font-medium text-gray-700">
                                {t('profile.field_vat')}
                            </Label>
                            <Input
                                id="pf-vat"
                                data-testid="portal-profile-vat-number"
                                value={form.vat_number || ''}
                                onChange={setField('vat_number')}
                                disabled={loading || saving}
                                placeholder="FR12345678901"
                                className={fieldClass}
                            />
                        </div>
                        <div className="sm:col-span-3 space-y-1.5">
                            <Label htmlFor="pf-siret" className="text-sm font-medium text-gray-700">
                                {t('profile.field_siret')}
                            </Label>
                            <Input
                                id="pf-siret"
                                data-testid="portal-profile-siret-number"
                                value={form.siret_number || ''}
                                onChange={setField('siret_number')}
                                disabled={loading || saving}
                                placeholder="123 456 789 00012"
                                className={fieldClass}
                            />
                        </div>

                        {/* Contact + phone */}
                        <div className="sm:col-span-3 space-y-1.5">
                            <Label htmlFor="pf-contact" className="text-sm font-medium text-gray-700">
                                {t('profile.field_contact')}
                            </Label>
                            <Input
                                id="pf-contact"
                                data-testid="portal-profile-contact-name"
                                value={form.contact_name || ''}
                                onChange={setField('contact_name')}
                                disabled={loading || saving}
                                className={fieldClass}
                                required
                            />
                        </div>
                        <div className="sm:col-span-3 space-y-1.5">
                            <Label className="text-sm font-medium text-gray-700">
                                {t('profile.field_phone')}
                            </Label>
                            <PortalPhoneInput
                                value={form.phone}
                                onChange={setField('phone')}
                                defaultCountry={form.country_code || 'FR'}
                                disabled={loading || saving}
                                testid="portal-profile-phone"
                            />
                            {phoneInvalid && (
                                <p className="text-[11px] text-red-500" data-testid="portal-profile-phone-error">
                                    {t('profile.phone_invalid')}
                                </p>
                            )}
                        </div>

                        {/* Address */}
                        <div className="sm:col-span-6 space-y-1.5">
                            <Label htmlFor="pf-addr1" className="text-sm font-medium text-gray-700">
                                {t('profile.field_address1')}
                            </Label>
                            <Input
                                id="pf-addr1"
                                data-testid="portal-profile-address-line1"
                                value={form.address_line1 || ''}
                                onChange={setField('address_line1')}
                                disabled={loading || saving}
                                className={fieldClass}
                                required
                            />
                        </div>
                        <div className="sm:col-span-6 space-y-1.5">
                            <Label htmlFor="pf-addr2" className="text-sm font-medium text-gray-700">
                                {t('profile.field_address2')}
                            </Label>
                            <Input
                                id="pf-addr2"
                                data-testid="portal-profile-address-line2"
                                value={form.address_line2 || ''}
                                onChange={setField('address_line2')}
                                disabled={loading || saving}
                                className={fieldClass}
                            />
                        </div>
                        <div className="sm:col-span-2 space-y-1.5">
                            <Label htmlFor="pf-postal" className="text-sm font-medium text-gray-700">
                                {t('profile.field_postal')}
                            </Label>
                            <Input
                                id="pf-postal"
                                data-testid="portal-profile-postal-code"
                                value={form.postal_code || ''}
                                onChange={setField('postal_code')}
                                disabled={loading || saving}
                                className={fieldClass}
                                required
                            />
                        </div>
                        <div className="sm:col-span-2 space-y-1.5">
                            <Label htmlFor="pf-city" className="text-sm font-medium text-gray-700">
                                {t('profile.field_city')}
                            </Label>
                            <Input
                                id="pf-city"
                                data-testid="portal-profile-city"
                                value={form.city || ''}
                                onChange={setField('city')}
                                disabled={loading || saving}
                                className={fieldClass}
                                required
                            />
                        </div>
                        <div className="sm:col-span-2 space-y-1.5">
                            <Label className="text-sm font-medium text-gray-700">
                                {t('profile.field_country')}
                            </Label>
                            <CountrySelect
                                value={form.country_code || ''}
                                onChange={(code, label) =>
                                    setForm((f) => ({ ...f, country_code: code, country: label }))
                                }
                                disabled={loading || saving}
                                testid="portal-profile-country"
                            />
                        </div>

                        <div className="sm:col-span-3 space-y-1.5">
                            <Label htmlFor="pf-locale" className="text-sm font-medium text-gray-700">
                                {t('profile.field_locale')}
                            </Label>
                            <Select
                                value={form.preferred_locale || 'en'}
                                onValueChange={(v) => setField('preferred_locale')(v)}
                            >
                                <SelectTrigger
                                    id="pf-locale"
                                    data-testid="portal-profile-preferred-locale"
                                    className="bg-[#FBF8F6] border-[#EFE5E1]"
                                >
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="en" data-testid="locale-option-en">English</SelectItem>
                                    <SelectItem value="fr" data-testid="locale-option-fr">Français</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <div className="flex items-center justify-end gap-3 mt-8 pt-6 border-t border-[#EFE5E1]">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => navigate('/portal')}
                            disabled={saving}
                            className="rounded-full"
                            data-testid="portal-profile-cancel-btn"
                        >
                            {t('common.cancel')}
                        </Button>
                        <Button
                            type="submit"
                            disabled={saving}
                            data-testid="portal-profile-save-btn"
                            className="bg-[#1A1A1A] hover:bg-[#333] text-white rounded-full px-6"
                        >
                            {saving ? (
                                <><Loader2 className="w-4 h-4 animate-spin mr-2" /> {t('profile.cta_saving')}</>
                            ) : (
                                <><Save size={16} className="mr-2" /> {t('profile.cta_save')}</>
                            )}
                        </Button>
                    </div>
                </form>
            </div>
        </PortalLayout>
    );
};

export default PortalProfile;
